import {
  File,
  StoragePathType,
  useGenerateUploadUrlMutation,
  useGetAllowedUploadSpecificationsQuery,
  useMakeFilePublicMutation,
  useUploadFileFromUrlMutation,
} from "@axio/graphql/generated/graphql";
import * as DocumentPicker from "expo-document-picker";
import axios from "axios";
import { useAppConfig } from "../AppConfigProvider";
import { useEffect } from "react";
import { MIME_TO_EXTENSION_MAP } from "../components/file/FileUploadComponent/fileTypeHelper";

export const generatePublicUrl = (currentDomain: string, filePath: string) => {
  let publicUrl = "";
  if (currentDomain === "axiodev.xyz") {
    publicUrl = `http://localhost:4443/storage/v1/b/primerai-dev-storage/o/${filePath}?alt=media`;
  } else {
    publicUrl = `https://${currentDomain}/${filePath}`;
  }

  return publicUrl;
};

export interface ProcessedFile {
  file: File;
  signedUrl: string;
  mimeType: string;
  size: number;
  localUploadedFile: DocumentPicker.DocumentPickerAsset;
}

export type UseFileUploadHookProps = {
  storagePath: StoragePathType;
  beforeUpload?: () => void;
  afterUpload?: (processedFilesArray: ProcessedFile[]) => void;
  onError?: (message?: string) => void;
  isPublic?: boolean;
  multiple?: boolean;
  allowedMimeTypesOverride?: string[];
  additionalAllowedMimeTypes?: string[];
  allowedFileExtensions?: string[];
};

const useFileUpload = ({
  storagePath,
  beforeUpload = () => {},
  afterUpload = (processedFilesArray: ProcessedFile[]) => {},
  onError = (message?: string) => {},
  isPublic = false,
  multiple = false,
  allowedMimeTypesOverride,
  additionalAllowedMimeTypes = [],
  allowedFileExtensions = [],
}: UseFileUploadHookProps) => {
  const { currentDomain } = useAppConfig();
  const [generateUploadUrl] = useGenerateUploadUrlMutation();
  const [uploadFileFromUrl] = useUploadFileFromUrlMutation();
  const [makeFilePublic] = useMakeFilePublicMutation();

  const { data: uploadSpecificationsData, refetch: uploadSpecificationsRefetch } =
    useGetAllowedUploadSpecificationsQuery({
      variables: {
        storagePath,
      },
      skip: !storagePath,
    });

  useEffect(() => {
    if (storagePath) {
      uploadSpecificationsRefetch();
    }
  }, [storagePath, uploadSpecificationsRefetch]);

  const serverAllowedMimeTypes = uploadSpecificationsData?.getAllowedUploadSpecifications?.data?.allowedMimeTypes ?? [];
  const allowedMimeTypes = Array.from(
    new Set([...(allowedMimeTypesOverride ?? serverAllowedMimeTypes), ...additionalAllowedMimeTypes])
  );
  const maxFileSize = uploadSpecificationsData?.getAllowedUploadSpecifications?.data?.maxFileSize ?? 0;
  const normalizedAllowedExtensions = allowedFileExtensions.map((extension) =>
    extension.toLowerCase().replace(".", "")
  );

  const getFileExtension = (fileName?: string | null) => fileName?.split(".").pop()?.toLowerCase() ?? "";

  const validateFile = (mimeType: string, size: number, fileName?: string | null) => {
    // Only enforce size constraint when backend provided a positive limit
    const isSizeConstraintEnabled = !!maxFileSize && maxFileSize > 0;
    if (isSizeConstraintEnabled && size > maxFileSize) {
      onError?.(
        `File size must be under ${maxFileSize / 1024 / 1024}MB. Yours is ${(size / 1024 / 1024).toFixed(2)}MB.`
      );
      return false;
    }
    // Only enforce type constraint when backend provided accepted MIME types
    const isTypeConstraintEnabled = Array.isArray(allowedMimeTypes) && allowedMimeTypes.length > 0;
    const isExtensionAllowed = normalizedAllowedExtensions.includes(getFileExtension(fileName));
    if (isTypeConstraintEnabled && !allowedMimeTypes.includes(mimeType) && !isExtensionAllowed) {
      onError?.(
        `Invalid file type. Accepted file types: ${[
          ...(allowedMimeTypes as string[]).map((mime) => MIME_TO_EXTENSION_MAP[mime]).filter(Boolean),
          ...normalizedAllowedExtensions,
        ].join(", ")}.`
      );
      return false;
    }
    return true;
  };

  const handleServerUpload = async ({
    uri,
    mimeType,
    size,
    name,
    onProgress,
  }: {
    uri: string;
    mimeType: string;
    size: number;
    name: string;
    onProgress?: (progress: number) => void;
  }) => {
    try {
      if (!validateFile(mimeType, size, name)) {
        return null;
      }

      const data = await generateUploadUrl({
        variables: {
          fileName: name,
          storagePath: storagePath,
        },
      });

      const uploadData = data?.data?.generateUploadUrl?.data[0];
      const { file, signedUrl, uploadedFileMimeType } = uploadData || {};

      if (!signedUrl) throw new Error("Failed to get signed URL");

      // Upload file to GCP using signed URL
      const fileData = await fetch(uri);
      const blob = await fileData.blob();

      await axios.put(signedUrl, blob, {
        headers: {
          "Content-Type": uploadedFileMimeType || mimeType,
        },
        onUploadProgress: (progressEvent) => {
          if (typeof progressEvent.total === "number" && progressEvent.total > 0) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (onProgress) onProgress(percentCompleted);
          }
        },
      });

      if (isPublic && currentDomain !== "axiodev.xyz") {
        const result = await makeFilePublic({
          variables: {
            fileId: file.id,
          },
        });

        if (!result.data?.makeFilePublic?.success) {
          throw new Error("Failed to make file public");
        }
      }

      return { file: file as File, signedUrl };
    } catch (error) {
      onError?.(error?.message || "An error occurred during file upload");
      return null;
    }
  };

  const handleServerUploadFromUrl = async ({ url, name }: { url: string; name?: string }) => {
    try {
      const response = await uploadFileFromUrl({
        variables: {
          url,
          storagePath,
          fileName: name,
        },
      });
      const payload = response.data?.uploadFileFromUrl;
      const file = payload?.data?.[0];

      if (!payload?.success || !file) {
        throw new Error(payload?.message ?? "Failed to upload file from URL");
      }

      if (isPublic && currentDomain !== "axiodev.xyz") {
        const result = await makeFilePublic({
          variables: {
            fileId: file.id,
          },
        });

        if (!result.data?.makeFilePublic?.success) {
          throw new Error("Failed to make file public");
        }
      }

      return { file: file as File };
    } catch (error) {
      onError?.(error?.message || "An error occurred during URL file upload");
      return null;
    }
  };

  const handleFileUpload = async () => {
    await beforeUpload();
    try {
      const pickerTypes = [...allowedMimeTypes, ...normalizedAllowedExtensions.map((extension) => `.${extension}`)];
      const result = await DocumentPicker.getDocumentAsync({
        // When constraints haven't loaded yet, allow all types in the picker
        type: pickerTypes.length > 0 ? pickerTypes.join(",") : "*/*",
        copyToCacheDirectory: true,
        multiple,
      });

      if (result.canceled || !result.assets) {
        return [];
      }

      const processedFilesArray: {
        file: File;
        signedUrl: string;
        mimeType: string;
        size: number;
        localUploadedFile: DocumentPicker.DocumentPickerAsset;
      }[] = [];

      for (const asset of result.assets) {
        const { uri, mimeType, size, name } = asset;

        if (!validateFile(mimeType, size, name)) {
          return [];
        }

        const uploadResult = await handleServerUpload({ uri, mimeType, size, name });
        if (!uploadResult) return [];
        const { file, signedUrl } = uploadResult;

        processedFilesArray.push({
          file: file,
          signedUrl,
          size,
          mimeType,
          localUploadedFile: asset,
        });
      }

      await afterUpload(processedFilesArray);

      return processedFilesArray;
    } catch (error) {
      onError?.(error?.message || "An error occurred during file upload");
    }
  };

  return { handleFileUpload, handleServerUpload, handleServerUploadFromUrl, allowedMimeTypes, maxFileSize };
};

export default useFileUpload;
